"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.hexToArrayBuffer = hexToArrayBuffer;
exports.arrayBufferToHex = arrayBufferToHex;
exports.mutateKey = mutateKey;
exports.generateKey = generateKey;
exports.encryptData = encryptData;
exports.decryptData = decryptData;
/**
 * @copyright
 * Copyright (c) 2025 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
// Get crypto.subtle in both environments
const getCrypto = () => {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (typeof window !== "undefined" && window.crypto) {
        return window.crypto;
    }
    // Node.js 15+
    return globalThis.crypto;
};
const crypto = getCrypto();
const ALGORITHM_NAME = "AES-GCM";
const KEY_LENGTH = 16; // 128 bits
const PADDED_KEY_LENGTH = 32;
const IV_LENGTH = 16; // 128 bits
// make sure we have corresponding mapping for the updated KEY_VERSION
const KEY_VERSION = "1";
const KEYVERSION_XORHEX_MAP = {
    "1": "1e452efa",
};
// Helper functions
function hexToArrayBuffer(hex) {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
        bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
    }
    return bytes.buffer;
}
function arrayBufferToHex(buffer) {
    return Array.from(new Uint8Array(buffer))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
}
function xorBuffer(dataBuffer, keyBuffer) {
    const result = new Uint8Array(dataBuffer);
    const keyArray = new Uint8Array(keyBuffer);
    for (let i = 0; i < result.length; i++) {
        result[i] ^= keyArray[i % keyArray.length];
    }
    return result.buffer;
}
/**
 * Applies a Caesar cipher shift to a Buffer.
 * Preserves case and leaves non-alphabetic characters unchanged.
 *
 * @param {Buffer} inputBuffer The input buffer to encrypt/decrypt.
 * @param {number} shift The number of positions to shift (positive for encryption, negative for decryption).
 * @returns {Buffer} The resulting ciphered buffer.
 */
function caesarCipherBuffer(inputBuffer, shift) {
    // Ensure the shift value is within a reasonable range (0-25)
    shift = shift % 26;
    if (shift < 0) {
        shift += 26; // Handle negative shifts
    }
    const resultBuffer = new Uint8Array(inputBuffer);
    for (let i = 0; i < resultBuffer.length; i++) {
        let byte = resultBuffer[i];
        // Handle uppercase letters (ASCII 65-90)
        if (byte >= 65 && byte <= 90) {
            byte = ((byte - 65 + shift) % 26) + 65;
        }
        // Handle lowercase letters (ASCII 97-122)
        else if (byte >= 97 && byte <= 122) {
            byte = ((byte - 97 + shift) % 26) + 97;
        }
        // Leave non-alphabetic characters unchanged
        resultBuffer[i] = byte;
    }
    return resultBuffer.buffer;
}
function mutateKey(paddedkey) {
    // use the last part of paddedkey
    try {
        const version = parseInt(paddedkey.slice(0, 3)).toString();
        const key = paddedkey.slice(-KEY_LENGTH * 2);
        const keyBuffer = hexToArrayBuffer(key);
        const xorKeyBuffer = hexToArrayBuffer(KEYVERSION_XORHEX_MAP[version]);
        const xoredBuffer1 = xorBuffer(keyBuffer, xorKeyBuffer);
        const xoredBuffer2 = xorBuffer(xoredBuffer1, hexToArrayBuffer(key[10]));
        return caesarCipherBuffer(xoredBuffer2, 11);
    }
    catch (error) {
        throw new Error("invalid key");
    }
}
/**
 * Generates a random encryption key
 * @returns {string} Hex-encoded key
 */
function generateKey() {
    const keyHex = arrayBufferToHex(crypto.getRandomValues(new Uint8Array(PADDED_KEY_LENGTH)).buffer);
    const keyPrefix = String(KEY_VERSION).padStart(3, "0");
    return keyHex.replace(/^.{3}/, keyPrefix);
}
// Encrypt
async function encryptData(text, keyHex) {
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    const mutatedKeyBuffer = mutateKey(keyHex);
    const cryptoKey = await crypto.subtle.importKey("raw", mutatedKeyBuffer, { name: ALGORITHM_NAME, length: KEY_LENGTH * 8 }, false, ["encrypt"]);
    const encryptedBuffer = await crypto.subtle.encrypt({ name: ALGORITHM_NAME, iv: iv }, cryptoKey, new TextEncoder().encode(text));
    const encryptedData = new Uint8Array(encryptedBuffer);
    const combined = new Uint8Array(iv.length + encryptedData.length);
    combined.set(iv, 0);
    combined.set(encryptedData, iv.length);
    return combined;
}
// Decrypt
async function decryptData(encryptedBuffer, keyHex) {
    if (encryptedBuffer.length < IV_LENGTH) {
        throw new Error("encrypted length should be greater than 0");
    }
    const ivArray = encryptedBuffer.slice(0, IV_LENGTH);
    const encryptedArray = encryptedBuffer.slice(IV_LENGTH);
    const mutatedKeyBuffer = mutateKey(keyHex);
    const cryptoKey = await crypto.subtle.importKey("raw", mutatedKeyBuffer, { name: ALGORITHM_NAME, length: KEY_LENGTH * 8 }, false, ["decrypt"]);
    const decryptedBuffer = await crypto.subtle.decrypt({ name: ALGORITHM_NAME, iv: ivArray }, cryptoKey, encryptedArray);
    return new TextDecoder().decode(decryptedBuffer);
}
